import { montanteColunaC, type PerfilVeiculo } from './perfilVeiculo'
import { postoDoVeiculo } from './configuracaoVeiculo'
import { rotuloLadoVeiculo } from './rotulosLado'
import { pontosMontagem, validarLigacao, type LigacaoEstabilizacao } from './montagemEstabilizacao'

/** Conteúdo rastreável à aula "Estabilização Veicular" fornecida pelo instrutor. */
export type PosicaoEstabilizacao = 'quatroRodas' | 'tombado' | 'capotado' | 'sobreOutroVeiculo'
export type EquipamentoEstabilizacao = 'calco' | 'bloco' | 'cunha' | 'fitaCatraca' | 'vstrut'
export type EtapaEstabilizacao = 'primaria' | 'secundaria'

export interface PontoSolo {
  x: number
  z: number
}

export interface ColocacaoEquipamento {
  id: string
  equipamento: EquipamentoEstabilizacao
  posicao: PontoSolo
  rotacaoGraus?: number
  comCunhaAjuste?: boolean
  invertido?: boolean
  composicaoBloco?: boolean
  /** Ausente nas cenas antigas; a montagem interativa exige ligação e tensão explícitas. */
  ligacao?: LigacaoEstabilizacao
}

export type TipoZonaEstabilizacao = 'roda' | 'estrutura' | 'vazio' | 'escora'

export interface ZonaEstabilizacao {
  id: string
  nome: string
  tipo: TipoZonaEstabilizacao
  equipamento: EquipamentoEstabilizacao
  equipamentosCompativeis: EquipamentoEstabilizacao[]
  etapa: EtapaEstabilizacao
  centro: PontoSolo
  pontoSolo?: [number, number, number]
  pontoVeiculo?: [number, number, number]
  dimensoesVazio?: [number, number, number]
  raio: number
  obrigatoria: boolean
  rotacaoPadraoGraus?: number
  invertivel?: boolean
  invertidoPadrao?: boolean
  ajuda?: string
}

export interface VetorInstabilidade {
  id: string
  origem: [number, number, number]
  destino: [number, number, number]
  leitura: string
}

export interface DefinicaoEquipamento {
  id: EquipamentoEstabilizacao
  nome: string
  funcao: string
}

export interface DefinicaoPosicao {
  id: PosicaoEstabilizacao
  nivel: number
  nome: string
  objetivo: string
  observacao: string
}

export const EQUIPAMENTOS_ESTABILIZACAO: Record<EquipamentoEstabilizacao, DefinicaoEquipamento> = {
  calco: { id: 'calco', nome: 'Calço escalonado', funcao: 'Apoio vertical rápido sob a base das colunas (caixa de ar) para anular a suspensão.' },
  bloco: { id: 'bloco', nome: 'Bloco', funcao: 'Preenche vazios estruturais maiores com apoio modular empilhável antiderrapante.' },
  cunha: { id: 'cunha', nome: 'Cunha de resgate', funcao: 'Bloqueio horizontal de rolamento nas rodas e ajuste de aperto final sob calços.' },
  fitaCatraca: { id: 'fitaCatraca', nome: 'Fita catraca', funcao: 'Une veículo e apoio em um conjunto monobloco.' },
  vstrut: { id: 'vstrut', nome: 'V-Strut', funcao: 'Cria apoio lateral regulável em posições instáveis.' },
}

export const POSICOES_ESTABILIZACAO: Record<PosicaoEstabilizacao, DefinicaoPosicao> = {
  quatroRodas: {
    id: 'quatroRodas',
    nivel: 1,
    nome: '1. Sobre quatro rodas',
    objetivo: 'Impedir rolamento com 2 cunhas por roda em rodas diagonalmente opostas e anular a suspensão com calçamento nas colunas A, B e C.',
    observacao: 'Exige bloqueio horizontal em 2 rodas opostas (4 cunhas no total) e estabilização vertical em 3 ou 4 pontos nas colunas.',
  },
  tombado: {
    id: 'tombado',
    nivel: 2,
    nome: '2. Tombado',
    objetivo: 'Escorar o assoalho com 2 V-Struts ancorados com fita catraca e calçar as colunas A e C no lado apoiado com calços escalonados.',
    observacao: 'O lado apoiado (colunas A e C) recebe calços escalonados contra o solo; o assoalho recebe escoras V-Strut com cinta catraca presa na soleira.',
  },
  capotado: {
    id: 'capotado',
    nivel: 3,
    nome: '3. Capotado',
    objetivo: 'Apoiar a estrutura entre o motor e a coluna A com blocos e cunha superior, e anular o efeito gangorra sob o teto/traseira com calços escalonados ou escoras.',
    observacao: 'Os blocos entre o motor e a coluna A recebem cunha de ajuste no topo (sem calço escalonado); sob o teto/traseira entram calços escalonados ou escoras.',
  },
  sobreOutroVeiculo: {
    id: 'sobreOutroVeiculo',
    nivel: 4,
    nome: '4. Cenário complexo',
    objetivo: 'Estabilizar o veículo inferior, preencher os vazios e fixar o conjunto como monobloco com fitas catraca.',
    observacao: 'Observe a direção de movimento e escolha pontos de fixação coerentes no próprio conjunto.',
  },
}

export const IDS_POSICAO_ESTABILIZACAO = Object.keys(POSICOES_ESTABILIZACAO) as PosicaoEstabilizacao[]
export const IDS_EQUIPAMENTO_ESTABILIZACAO = Object.keys(EQUIPAMENTOS_ESTABILIZACAO) as EquipamentoEstabilizacao[]

interface PropsCriarZona {
  id: string
  nome: string
  tipo?: TipoZonaEstabilizacao
  equipamento: EquipamentoEstabilizacao
  equipamentosCompativeis?: EquipamentoEstabilizacao[]
  etapa: EtapaEstabilizacao
  centro: PontoSolo
  pontoSolo?: [number, number, number]
  pontoVeiculo?: [number, number, number]
  dimensoesVazio?: [number, number, number]
  raio?: number
  obrigatoria?: boolean
  rotacaoPadraoGraus?: number
  invertivel?: boolean
  invertidoPadrao?: boolean
  ajuda?: string
}

const zona = (p: PropsCriarZona): ZonaEstabilizacao => ({
  id: p.id,
  nome: p.nome,
  tipo: p.tipo ?? 'estrutura',
  equipamento: p.equipamento,
  equipamentosCompativeis: p.equipamentosCompativeis ?? [p.equipamento],
  etapa: p.etapa,
  centro: p.centro,
  pontoSolo: p.pontoSolo ?? [p.centro.x, 0, p.centro.z],
  pontoVeiculo: p.pontoVeiculo,
  dimensoesVazio: p.dimensoesVazio,
  raio: p.raio ?? 0.55,
  obrigatoria: p.obrigatoria ?? true,
  rotacaoPadraoGraus: p.rotacaoPadraoGraus ?? 0,
  invertivel: p.invertivel,
  invertidoPadrao: p.invertidoPadrao,
  ajuda: p.ajuda,
})

/** Quanto a base da escora fica para fora do veículo. */
const AFASTAMENTO_ESCORA = 0.66
/** Quanto o ponto de ancoragem da fita fica para fora do veículo. */
const AFASTAMENTO_AMARRA = 0.96

export function zonasDaEstabilizacao(
  posicao: PosicaoEstabilizacao,
  perfil: PerfilVeiculo,
): readonly ZonaEstabilizacao[] {
  const meiaLargura = perfil.largura / 2
  const frente = perfil.eixoDianteiroZ
  const traseira = perfil.eixoTraseiroZ
  const meioEntreEixos = (frente + traseira) / 2
  const xEstrutura = meiaLargura - 0.08
  const xRodaEsq = -(meiaLargura - perfil.larguraRoda / 2 + 0.02)
  const xRodaDir = meiaLargura - perfil.larguraRoda / 2 + 0.02
  const distCunhaPneu = perfil.raioRoda * 0.92
  // o apoio da coluna C vai sob a soleira, no pé do montante: à frente da roda
  // traseira, com folga para o calço não encostar no pneu
  const zApoioColunaC = montanteColunaC(perfil).zPe + 0.14
  // o apoio da coluna A vai no pé dela, na linha das dobradiças da porta
  // dianteira, com a mesma folga de pneu do apoio da C
  const zApoioColunaA = Math.min(
    perfil.portaDianteira.zDianteiro,
    perfil.eixoDianteiroZ - perfil.raioRoda - 0.18,
  )

  if (posicao === 'quatroRodas') {
    return [
      // Estabilização Horizontal: 8 pontos de cunha (frente e trás de cada uma das 4 rodas)
      // Dianteira Esquerda (Motorista)
      zona({
        id: 'cunha-roda-diant-esq-frente',
        nome: `cunha dianteira do ${rotuloLadoVeiculo('esquerdo')} (frente)`,
        tipo: 'roda',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha'],
        etapa: 'primaria',
        centro: { x: xRodaEsq, z: frente + distCunhaPneu },
        pontoSolo: [xRodaEsq, 0, frente + distCunhaPneu],
        pontoVeiculo: [xRodaEsq, 0.05, frente + distCunhaPneu * 0.6],
        raio: 0.45,
        obrigatoria: false,
        rotacaoPadraoGraus: 90,
        ajuda: `Cunha bloqueando a roda dianteira do ${rotuloLadoVeiculo('esquerdo')} pela frente`,
      }),
      zona({
        id: 'cunha-roda-diant-esq-tras',
        nome: `cunha dianteira do ${rotuloLadoVeiculo('esquerdo')} (trás)`,
        tipo: 'roda',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha'],
        etapa: 'primaria',
        centro: { x: xRodaEsq, z: frente - distCunhaPneu },
        pontoSolo: [xRodaEsq, 0, frente - distCunhaPneu],
        pontoVeiculo: [xRodaEsq, 0.05, frente - distCunhaPneu * 0.6],
        raio: 0.45,
        obrigatoria: false,
        rotacaoPadraoGraus: 270,
        ajuda: `Cunha bloqueando a roda dianteira do ${rotuloLadoVeiculo('esquerdo')} por trás`,
      }),
      // Dianteira Direita (Passageiro)
      zona({
        id: 'cunha-roda-diant-dir-frente',
        nome: `cunha dianteira do ${rotuloLadoVeiculo('direito')} (frente)`,
        tipo: 'roda',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha'],
        etapa: 'primaria',
        centro: { x: xRodaDir, z: frente + distCunhaPneu },
        pontoSolo: [xRodaDir, 0, frente + distCunhaPneu],
        pontoVeiculo: [xRodaDir, 0.05, frente + distCunhaPneu * 0.6],
        raio: 0.45,
        obrigatoria: false,
        rotacaoPadraoGraus: 90,
        ajuda: `Cunha bloqueando a roda dianteira do ${rotuloLadoVeiculo('direito')} pela frente`,
      }),
      zona({
        id: 'cunha-roda-diant-dir-tras',
        nome: `cunha dianteira do ${rotuloLadoVeiculo('direito')} (trás)`,
        tipo: 'roda',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha'],
        etapa: 'primaria',
        centro: { x: xRodaDir, z: frente - distCunhaPneu },
        pontoSolo: [xRodaDir, 0, frente - distCunhaPneu],
        pontoVeiculo: [xRodaDir, 0.05, frente - distCunhaPneu * 0.6],
        raio: 0.45,
        obrigatoria: false,
        rotacaoPadraoGraus: 270,
        ajuda: `Cunha bloqueando a roda dianteira do ${rotuloLadoVeiculo('direito')} por trás`,
      }),
      // Traseira Esquerda (Motorista)
      zona({
        id: 'cunha-roda-tras-esq-frente',
        nome: `cunha traseira do ${rotuloLadoVeiculo('esquerdo')} (frente)`,
        tipo: 'roda',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha'],
        etapa: 'primaria',
        centro: { x: xRodaEsq, z: traseira + distCunhaPneu },
        pontoSolo: [xRodaEsq, 0, traseira + distCunhaPneu],
        pontoVeiculo: [xRodaEsq, 0.05, traseira + distCunhaPneu * 0.6],
        raio: 0.45,
        obrigatoria: false,
        rotacaoPadraoGraus: 90,
        ajuda: `Cunha bloqueando a roda traseira do ${rotuloLadoVeiculo('esquerdo')} pela frente`,
      }),
      zona({
        id: 'cunha-roda-tras-esq-tras',
        nome: `cunha traseira do ${rotuloLadoVeiculo('esquerdo')} (trás)`,
        tipo: 'roda',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha'],
        etapa: 'primaria',
        centro: { x: xRodaEsq, z: traseira - distCunhaPneu },
        pontoSolo: [xRodaEsq, 0, traseira - distCunhaPneu],
        pontoVeiculo: [xRodaEsq, 0.05, traseira - distCunhaPneu * 0.6],
        raio: 0.45,
        obrigatoria: false,
        rotacaoPadraoGraus: 270,
        ajuda: `Cunha bloqueando a roda traseira do ${rotuloLadoVeiculo('esquerdo')} por trás`,
      }),
      // Traseira Direita (Passageiro)
      zona({
        id: 'cunha-roda-tras-dir-frente',
        nome: `cunha traseira do ${rotuloLadoVeiculo('direito')} (frente)`,
        tipo: 'roda',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha'],
        etapa: 'primaria',
        centro: { x: xRodaDir, z: traseira + distCunhaPneu },
        pontoSolo: [xRodaDir, 0, traseira + distCunhaPneu],
        pontoVeiculo: [xRodaDir, 0.05, traseira + distCunhaPneu * 0.6],
        raio: 0.45,
        obrigatoria: false,
        rotacaoPadraoGraus: 90,
        ajuda: `Cunha bloqueando a roda traseira do ${rotuloLadoVeiculo('direito')} pela frente`,
      }),
      zona({
        id: 'cunha-roda-tras-dir-tras',
        nome: `cunha traseira do ${rotuloLadoVeiculo('direito')} (trás)`,
        tipo: 'roda',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha'],
        etapa: 'primaria',
        centro: { x: xRodaDir, z: traseira - distCunhaPneu },
        pontoSolo: [xRodaDir, 0, traseira - distCunhaPneu],
        pontoVeiculo: [xRodaDir, 0.05, traseira - distCunhaPneu * 0.6],
        raio: 0.45,
        obrigatoria: false,
        rotacaoPadraoGraus: 270,
        ajuda: `Cunha bloqueando a roda traseira do ${rotuloLadoVeiculo('direito')} por trás`,
      }),

      // Estabilização Vertical sob a Estrutura (Colunas A, B, C na Soleira)
      zona({
        id: 'calco-coluna-a-esq',
        nome: 'apoio coluna A motorista',
        tipo: 'estrutura',
        equipamento: 'calco',
        equipamentosCompativeis: ['calco', 'bloco'],
        etapa: 'primaria',
        centro: { x: -xEstrutura, z: zApoioColunaA },
        pontoSolo: [-xEstrutura, 0, zApoioColunaA],
        pontoVeiculo: [-xEstrutura, perfil.topoSoleiraY - 0.06, zApoioColunaA],
        dimensoesVazio: [0.18, perfil.topoSoleiraY, 0.5],
        raio: 0.52,
        obrigatoria: false,
        rotacaoPadraoGraus: 0,
        ajuda: 'Apoio vertical sob a base da coluna A (lado motorista)',
      }),
      zona({
        id: 'calco-coluna-b-esq',
        nome: 'apoio coluna B motorista',
        tipo: 'estrutura',
        equipamento: 'calco',
        equipamentosCompativeis: ['calco', 'bloco'],
        etapa: 'primaria',
        centro: { x: -xEstrutura, z: perfil.colunaB.z },
        pontoSolo: [-xEstrutura, 0, perfil.colunaB.z],
        pontoVeiculo: [-xEstrutura, perfil.topoSoleiraY - 0.06, perfil.colunaB.z],
        dimensoesVazio: [0.18, perfil.topoSoleiraY, 0.5],
        raio: 0.52,
        obrigatoria: false,
        rotacaoPadraoGraus: 0,
        ajuda: 'Apoio vertical sob a base da coluna B (lado motorista)',
      }),
      zona({
        id: 'calco-coluna-c-esq',
        nome: 'apoio coluna C motorista',
        tipo: 'estrutura',
        equipamento: 'calco',
        equipamentosCompativeis: ['calco', 'bloco'],
        etapa: 'primaria',
        centro: { x: -xEstrutura, z: zApoioColunaC },
        pontoSolo: [-xEstrutura, 0, zApoioColunaC],
        pontoVeiculo: [-xEstrutura, perfil.topoSoleiraY - 0.06, zApoioColunaC],
        dimensoesVazio: [0.18, perfil.topoSoleiraY, 0.5],
        raio: 0.52,
        obrigatoria: false,
        rotacaoPadraoGraus: 0,
        ajuda: 'Apoio vertical sob a base da coluna C (lado motorista)',
      }),
      zona({
        id: 'calco-coluna-a-dir',
        nome: 'apoio coluna A passageiro',
        tipo: 'estrutura',
        equipamento: 'calco',
        equipamentosCompativeis: ['calco', 'bloco'],
        etapa: 'primaria',
        centro: { x: xEstrutura, z: zApoioColunaA },
        pontoSolo: [xEstrutura, 0, zApoioColunaA],
        pontoVeiculo: [xEstrutura, perfil.topoSoleiraY - 0.06, zApoioColunaA],
        dimensoesVazio: [0.18, perfil.topoSoleiraY, 0.5],
        raio: 0.52,
        obrigatoria: false,
        rotacaoPadraoGraus: 180,
        ajuda: 'Apoio vertical sob a base da coluna A (lado passageiro)',
      }),
      zona({
        id: 'calco-coluna-b-dir',
        nome: 'apoio coluna B passageiro',
        tipo: 'estrutura',
        equipamento: 'calco',
        equipamentosCompativeis: ['calco', 'bloco'],
        etapa: 'primaria',
        centro: { x: xEstrutura, z: perfil.colunaB.z },
        pontoSolo: [xEstrutura, 0, perfil.colunaB.z],
        pontoVeiculo: [xEstrutura, perfil.topoSoleiraY - 0.06, perfil.colunaB.z],
        dimensoesVazio: [0.18, perfil.topoSoleiraY, 0.5],
        raio: 0.52,
        obrigatoria: false,
        rotacaoPadraoGraus: 180,
        ajuda: 'Apoio vertical sob a base da coluna B (lado passageiro)',
      }),
      zona({
        id: 'calco-coluna-c-dir',
        nome: 'apoio coluna C passageiro',
        tipo: 'estrutura',
        equipamento: 'calco',
        equipamentosCompativeis: ['calco', 'bloco'],
        etapa: 'primaria',
        centro: { x: xEstrutura, z: zApoioColunaC },
        pontoSolo: [xEstrutura, 0, zApoioColunaC],
        pontoVeiculo: [xEstrutura, perfil.topoSoleiraY - 0.06, zApoioColunaC],
        dimensoesVazio: [0.18, perfil.topoSoleiraY, 0.5],
        raio: 0.52,
        obrigatoria: false,
        rotacaoPadraoGraus: 180,
        ajuda: 'Apoio vertical sob a base da coluna C (lado passageiro)',
      }),

      // Ajustes secundários
      zona({
        id: 'ajuste-bloco-esq',
        nome: `bloco de nivelamento do ${rotuloLadoVeiculo('esquerdo')}`,
        tipo: 'estrutura',
        equipamento: 'bloco',
        equipamentosCompativeis: ['bloco', 'calco'],
        etapa: 'secundaria',
        centro: { x: -xEstrutura, z: meioEntreEixos },
        pontoSolo: [-xEstrutura, 0, meioEntreEixos],
        pontoVeiculo: [-xEstrutura, perfil.topoSoleiraY - 0.06, meioEntreEixos],
        raio: 0.55,
        obrigatoria: false,
        rotacaoPadraoGraus: 0,
      }),
      zona({
        id: 'ajuste-bloco-dir',
        nome: `bloco de nivelamento do ${rotuloLadoVeiculo('direito')}`,
        tipo: 'estrutura',
        equipamento: 'bloco',
        equipamentosCompativeis: ['bloco', 'calco'],
        etapa: 'secundaria',
        centro: { x: xEstrutura, z: meioEntreEixos },
        pontoSolo: [xEstrutura, 0, meioEntreEixos],
        pontoVeiculo: [xEstrutura, perfil.topoSoleiraY - 0.06, meioEntreEixos],
        raio: 0.55,
        obrigatoria: false,
        rotacaoPadraoGraus: 0,
      }),
    ]
  }

  if (posicao === 'tombado') {
    // Veículo tombado sobre o lado esquerdo (lado do motorista contra o solo),
    // rolado −90° (`ROLAGEM_SOBRE_O_MOTORISTA_RAD`): no mundo o teto fica em +X
    // e o assoalho exposto em −X.
    // - Lado do teto (colunas A e C, x > 0): calços escalonados apoiando as colunas contra o solo
    // - Lado do Assoalho (exposto, x < 0): escoras V-Strut com pé no solo, cabeça cravada no assoalho e cinta na soleira
    const xColunaApoiada = perfil.tetoY - 0.1 // aprox. 1.35m (junto à coluna A e C apoiadas)
    const zColunaA = Number(((perfil.colunaA.zBase + perfil.colunaA.zTopo) / 2).toFixed(2))
    const zColunaC = Number(((perfil.colunaC.zBase + perfil.colunaC.zTopo) / 2).toFixed(2))
    const xBaseEscora = -0.48 // pé da escora no solo posicionado para angulação ideal de ~52° a 60°
    const yAssoalhoEscora = Math.min(1.38, perfil.largura - 0.32) // ponto alto de apoio nas longarinas do assoalho (~1.38m a 1.40m)
    const xAssoalho = 0.25 // superfície da chapa/longarina do assoalho
    const xLataColunaC = Number((perfil.cinturaY - 0.16).toFixed(2)) // lata estrutural junto à base do vidro da coluna C
    const yLataColunaC = Number(Math.min(1.48, perfil.largura - 0.24).toFixed(2))

    const pontoVeiculoEscoraDiant: [number, number, number] = [xAssoalho, yAssoalhoEscora, perfil.colunaA.zBase]
    const pontoVeiculoEscoraTras: [number, number, number] = [xLataColunaC, yLataColunaC, zColunaC]

    return [
      // LADO DO TETO: Calços escalonados sob as colunas A e C contra o solo
      zona({
        id: 'base-dianteira',
        nome: 'calço sob coluna A (lado apoiado)',
        tipo: 'vazio',
        equipamento: 'calco',
        equipamentosCompativeis: ['calco', 'bloco'],
        etapa: 'primaria',
        centro: { x: xColunaApoiada, z: zColunaA },
        pontoSolo: [xColunaApoiada, 0, zColunaA],
        pontoVeiculo: [xColunaApoiada - 0.12, 0.22, zColunaA],
        dimensoesVazio: [0.24, 0.22, 0.45],
        raio: 0.65,
        obrigatoria: true,
        rotacaoPadraoGraus: 270,
        ajuda: 'Calço escalonado apoiando sob a coluna A dianteira contra o solo com declive em direção à frente',
      }),
      zona({
        id: 'base-traseira',
        nome: 'calço sob coluna C (lado apoiado)',
        tipo: 'vazio',
        equipamento: 'calco',
        equipamentosCompativeis: ['calco', 'bloco'],
        etapa: 'primaria',
        centro: { x: xColunaApoiada, z: zColunaC },
        pontoSolo: [xColunaApoiada, 0, zColunaC],
        pontoVeiculo: [xColunaApoiada - 0.12, 0.22, zColunaC],
        dimensoesVazio: [0.24, 0.22, 0.45],
        raio: 0.65,
        obrigatoria: true,
        rotacaoPadraoGraus: 90,
        ajuda: 'Calço escalonado apoiando sob a coluna C traseira contra o solo com declive em direção à traseira',
      }),
      zona({
        id: 'folga-tombado',
        nome: 'cunha sob lateral / coluna B (lado apoiado)',
        tipo: 'vazio',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha', 'bloco'],
        etapa: 'secundaria',
        centro: { x: xColunaApoiada, z: meioEntreEixos },
        pontoSolo: [xColunaApoiada, 0, meioEntreEixos],
        pontoVeiculo: [xColunaApoiada - 0.08, 0.14, meioEntreEixos],
        dimensoesVazio: [0.22, 0.14, 0.35],
        raio: 0.55,
        obrigatoria: true,
        rotacaoPadraoGraus: 0,
        ajuda: 'Cunha para aperto fino e eliminação de folga residual',
      }),

      // LADO DO ASSOALHO: V-Struts de escoramento cravados no assoalho / longarinas
      zona({
        id: 'escora-dianteira',
        nome: 'V-Strut dianteiro (assoalho)',
        tipo: 'escora',
        equipamento: 'vstrut',
        equipamentosCompativeis: ['vstrut'],
        etapa: 'primaria',
        centro: { x: xBaseEscora, z: perfil.colunaA.zBase },
        pontoSolo: [xBaseEscora, 0, perfil.colunaA.zBase],
        pontoVeiculo: pontoVeiculoEscoraDiant,
        raio: 0.85,
        obrigatoria: true,
        rotacaoPadraoGraus: 270,
        ajuda: 'Escora V-Strut com pé no solo e cabeça travada no assoalho dianteiro, com cinta presa na soleira',
      }),
      zona({
        id: 'escora-traseira',
        nome: 'V-Strut traseiro (coluna C / lata)',
        tipo: 'escora',
        equipamento: 'vstrut',
        equipamentosCompativeis: ['vstrut'],
        etapa: 'primaria',
        centro: { x: xBaseEscora, z: perfil.colunaC.zBase },
        pontoSolo: [xBaseEscora, 0, perfil.colunaC.zBase],
        pontoVeiculo: pontoVeiculoEscoraTras,
        raio: 0.85,
        obrigatoria: true,
        rotacaoPadraoGraus: 270,
        ajuda: 'Escora V-Strut com pé no solo e cabeça cravada na lata próxima ao vidro da coluna C; cinta e gancho alinhados ancorados na lataria estrutural (sem ancorar na roda)',
      }),
      zona({
        id: 'amarra-tombado',
        nome: 'amarração / fita catraca complementar',
        tipo: 'estrutura',
        equipamento: 'fitaCatraca',
        equipamentosCompativeis: ['fitaCatraca'],
        etapa: 'secundaria',
        centro: { x: xBaseEscora - 0.3, z: meioEntreEixos },
        pontoSolo: [xBaseEscora - 0.3, 0, meioEntreEixos],
        pontoVeiculo: [xAssoalho, 0.35, meioEntreEixos],
        raio: 0.85,
        obrigatoria: true,
        rotacaoPadraoGraus: 270,
        ajuda: 'Fita catraca tensionando a base da escora à estrutura inferior',
      }),
    ]
  }

  const zDianteiro = (perfil.colunaA.zBase + perfil.frenteZ) / 2
  const zTraseiro = (perfil.colunaC.zBase + perfil.traseiraZ) / 2

  if (posicao === 'capotado') {
    // Veículo capotado (invertido com inclinação frontal):
    // 1. Bloco sob a estrutura dianteira entre o motor e a coluna A
    const zBlocoDianteiro = Number(((perfil.colunaA.zBase + perfil.colunaA.zTopo) * 0.42).toFixed(2))

    // 2. Calços escalonados inseridos sob o teto (colunas B/C) para anular a gangorra
    const xTeto = Number((meiaLargura - perfil.recuoLateralTeto).toFixed(2))
    const zTetoCapotado = Number(((perfil.colunaB.z + perfil.colunaC.zTopo) * 0.57).toFixed(2))

    // 3. Escoras V-Strut na lata próxima ao vidro da coluna C (uma de cada lado), travando a elevação da traseira
    const postoCapotado = postoDoVeiculo(perfil, 'invertido')
    const thetaCapotado = postoCapotado.inclinacaoLongitudinalRad
    const cosTheta = Math.cos(thetaCapotado)
    const sinTheta = Math.sin(thetaCapotado)

    // Ponto estrutural na lata do veículo junto à coluna C e vidro vigia (nunca ancorar na roda, pois ela movimenta e desestabiliza)
    // No referencial do veículo: z na coluna C / vidro traseiro, y na linha de cintura (lata abaixo do vidro)
    const zLocalEscora = Number(((perfil.portaTraseira.zTraseiro + perfil.colunaC.zTopo) / 2).toFixed(2))
    const yLocalEscora = Number((perfil.cinturaY - 0.05).toFixed(2))
    const xLocalEscora = Number((meiaLargura - perfil.recuoLateralTeto * 0.4).toFixed(2))
    const yContatoEscora = Number((postoCapotado.alturaApoio - yLocalEscora * cosTheta - zLocalEscora * sinTheta).toFixed(2))
    const zContatoEscora = Number((-yLocalEscora * sinTheta + zLocalEscora * cosTheta).toFixed(2))

    // Base no solo afastada lateralmente para manter a angulação técnica ideal (~50° a 58°)
    const afastamentoSoloCapotado = 0.52
    const xBaseSoloEsq = -Number((xLocalEscora + afastamentoSoloCapotado).toFixed(2))
    const xBaseSoloDir = Number((xLocalEscora + afastamentoSoloCapotado).toFixed(2))
    const zBaseSolo = Number((zContatoEscora + 0.08).toFixed(2))

    return [
      // DIANTEIRA: Bloco entre o motor e a coluna A com cunha de ajuste superior
      zona({
        id: 'capo-esquerdo',
        nome: `bloco com cunha sob estrutura dianteira do ${rotuloLadoVeiculo('esquerdo')} (entre motor e coluna A)`,
        tipo: 'vazio',
        equipamento: 'bloco',
        equipamentosCompativeis: ['bloco', 'calco', 'cunha'],
        etapa: 'primaria',
        centro: { x: -xEstrutura, z: zBlocoDianteiro },
        pontoSolo: [-xEstrutura, 0, zBlocoDianteiro],
        pontoVeiculo: [-xEstrutura, 0.24, zBlocoDianteiro],
        dimensoesVazio: [0.24, 0.24, 0.45],
        raio: 0.55,
        obrigatoria: true,
        rotacaoPadraoGraus: 0,
        invertivel: true,
        ajuda: 'Bloco com cunha de ajuste no topo posicionado entre o motor e a coluna A (sem calço escalonado em cima)',
      }),
      zona({
        id: 'capo-direito',
        nome: `bloco com cunha sob estrutura dianteira do ${rotuloLadoVeiculo('direito')} (entre motor e coluna A)`,
        tipo: 'vazio',
        equipamento: 'bloco',
        equipamentosCompativeis: ['bloco', 'calco', 'cunha'],
        etapa: 'primaria',
        centro: { x: xEstrutura, z: zBlocoDianteiro },
        pontoSolo: [xEstrutura, 0, zBlocoDianteiro],
        pontoVeiculo: [xEstrutura, 0.24, zBlocoDianteiro],
        dimensoesVazio: [0.24, 0.24, 0.45],
        raio: 0.55,
        obrigatoria: true,
        rotacaoPadraoGraus: 0,
        invertivel: true,
        ajuda: 'Bloco com cunha de ajuste no topo posicionado entre o motor e a coluna A (sem calço escalonado em cima)',
      }),

      // TETO/TRASEIRA: Calços escalonados inseridos sob o teto entre as colunas B e C
      zona({
        id: 'traseira-esquerda',
        nome: `calço escalonado sob o teto do ${rotuloLadoVeiculo('esquerdo')} (colunas B/C)`,
        tipo: 'vazio',
        equipamento: 'calco',
        equipamentosCompativeis: ['calco', 'bloco'],
        etapa: 'primaria',
        centro: { x: -xTeto, z: zTetoCapotado },
        pontoSolo: [-xTeto, 0, zTetoCapotado],
        pontoVeiculo: [-xTeto, 0.28, zTetoCapotado],
        dimensoesVazio: [0.20, 0.28, 0.65],
        raio: 0.58,
        obrigatoria: true,
        rotacaoPadraoGraus: 270,
        ajuda: 'Calço escalonado sob o teto para eliminar folgas e travar a gangorra',
      }),
      zona({
        id: 'traseira-direita',
        nome: `calço escalonado sob o teto do ${rotuloLadoVeiculo('direito')} (colunas B/C)`,
        tipo: 'vazio',
        equipamento: 'calco',
        equipamentosCompativeis: ['calco', 'bloco'],
        etapa: 'primaria',
        centro: { x: xTeto, z: zTetoCapotado },
        pontoSolo: [xTeto, 0, zTetoCapotado],
        pontoVeiculo: [xTeto, 0.28, zTetoCapotado],
        dimensoesVazio: [0.20, 0.28, 0.65],
        raio: 0.58,
        obrigatoria: true,
        rotacaoPadraoGraus: 270,
        ajuda: 'Calço escalonado sob o teto para eliminar folgas e travar a gangorra',
      }),

      // AJUSTES COM CUNHAS
      zona({
        id: 'ajuste-capotado-frente',
        nome: 'cunha de ajuste dianteira (trava do bloco/capô)',
        tipo: 'vazio',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha', 'bloco'],
        etapa: 'secundaria',
        centro: { x: 0, z: zBlocoDianteiro },
        pontoSolo: [0, 0, zBlocoDianteiro],
        pontoVeiculo: [0, 0.12, zBlocoDianteiro],
        dimensoesVazio: [0.2, 0.12, 0.3],
        raio: 0.52,
        obrigatoria: true,
        rotacaoPadraoGraus: 180,
      }),
      zona({
        id: 'ajuste-capotado-traseira',
        nome: 'cunha de ajuste sob o teto (trava do calço)',
        tipo: 'vazio',
        equipamento: 'cunha',
        equipamentosCompativeis: ['cunha', 'bloco'],
        etapa: 'secundaria',
        centro: { x: 0, z: zTetoCapotado },
        pontoSolo: [0, 0, zTetoCapotado],
        pontoVeiculo: [0, 0.12, zTetoCapotado],
        dimensoesVazio: [0.2, 0.12, 0.3],
        raio: 0.52,
        obrigatoria: true,
        rotacaoPadraoGraus: 0,
      }),

      // ESCORAS V-STRUT: Cravadas na lata próxima ao vidro da coluna C (uma de cada lado), travando a elevação da traseira
      zona({
        id: 'escora-capotado-esquerda',
        nome: `V-Strut traseiro do ${rotuloLadoVeiculo('esquerdo')} (lata da coluna C)`,
        tipo: 'escora',
        equipamento: 'vstrut',
        equipamentosCompativeis: ['vstrut'],
        etapa: 'secundaria',
        centro: { x: xBaseSoloEsq, z: zBaseSolo },
        pontoSolo: [xBaseSoloEsq, 0, zBaseSolo],
        pontoVeiculo: [-xLocalEscora, yContatoEscora, zContatoEscora],
        raio: 0.85,
        obrigatoria: false,
        rotacaoPadraoGraus: 90,
        ajuda: `Escora V-Strut com pé no solo e cabeça cravada na lata próxima ao vidro da coluna C do ${rotuloLadoVeiculo('esquerdo')}; cinta e gancho alinhados na lataria estrutural (sem ancorar na roda)`,
      }),
      zona({
        id: 'escora-capotado-direita',
        nome: `V-Strut traseiro do ${rotuloLadoVeiculo('direito')} (lata da coluna C)`,
        tipo: 'escora',
        equipamento: 'vstrut',
        equipamentosCompativeis: ['vstrut'],
        etapa: 'secundaria',
        centro: { x: xBaseSoloDir, z: zBaseSolo },
        pontoSolo: [xBaseSoloDir, 0, zBaseSolo],
        pontoVeiculo: [xLocalEscora, yContatoEscora, zContatoEscora],
        raio: 0.85,
        obrigatoria: false,
        rotacaoPadraoGraus: 270,
        ajuda: `Escora V-Strut com pé no solo e cabeça cravada na lata próxima ao vidro da coluna C do ${rotuloLadoVeiculo('direito')}; cinta e gancho alinhados na lataria estrutural (sem ancorar na roda)`,
      }),
    ]
  }

  // Cenário Complexo (Sobre outro veículo)
  const xFora = meiaLargura + AFASTAMENTO_ESCORA + 0.73
  return [
    zona({
      id: 'inferior-frente',
      nome: 'apoio dianteiro do veículo inferior',
      tipo: 'estrutura',
      equipamento: 'bloco',
      equipamentosCompativeis: ['bloco', 'calco'],
      etapa: 'primaria',
      centro: { x: -xEstrutura, z: zDianteiro },
      pontoSolo: [-xEstrutura, 0, zDianteiro],
      pontoVeiculo: [-xEstrutura, 0.28, zDianteiro],
      dimensoesVazio: [0.25, 0.28, 0.4],
      raio: 0.55,
      obrigatoria: true,
    }),
    zona({
      id: 'inferior-traseira',
      nome: 'apoio traseiro do veículo inferior',
      tipo: 'estrutura',
      equipamento: 'bloco',
      equipamentosCompativeis: ['bloco', 'calco'],
      etapa: 'primaria',
      centro: { x: xEstrutura, z: zTraseiro },
      pontoSolo: [xEstrutura, 0, zTraseiro],
      pontoVeiculo: [xEstrutura, 0.28, zTraseiro],
      dimensoesVazio: [0.25, 0.28, 0.4],
      raio: 0.55,
      obrigatoria: true,
    }),
    zona({
      id: 'vazio-entre-frente',
      nome: 'vazio intermediário dianteiro entre os veículos',
      tipo: 'vazio',
      equipamento: 'cunha',
      equipamentosCompativeis: ['cunha', 'bloco'],
      etapa: 'primaria',
      centro: { x: 0, z: perfil.colunaA.zBase },
      pontoSolo: [0, 0.85, perfil.colunaA.zBase],
      pontoVeiculo: [0, 1.05, perfil.colunaA.zBase],
      dimensoesVazio: [0.35, 0.2, 0.35],
      raio: 0.55,
      obrigatoria: true,
    }),
    zona({
      id: 'vazio-entre-traseira',
      nome: 'vazio intermediário traseiro entre os veículos',
      tipo: 'vazio',
      equipamento: 'cunha',
      equipamentosCompativeis: ['cunha', 'bloco'],
      etapa: 'primaria',
      centro: { x: 0, z: perfil.colunaC.zBase },
      pontoSolo: [0, 0.85, perfil.colunaC.zBase],
      pontoVeiculo: [0, 1.05, perfil.colunaC.zBase],
      dimensoesVazio: [0.35, 0.2, 0.35],
      raio: 0.55,
      obrigatoria: true,
    }),
    zona({
      id: 'monobloco-esquerdo',
      nome: `fita catraca monobloco do ${rotuloLadoVeiculo('esquerdo')}`,
      tipo: 'estrutura',
      equipamento: 'fitaCatraca',
      equipamentosCompativeis: ['fitaCatraca'],
      etapa: 'secundaria',
      centro: { x: -xFora, z: frente * 0.55 },
      pontoSolo: [-xFora, 0, frente * 0.55],
      pontoVeiculo: [-meiaLargura, 1.2, frente * 0.55],
      raio: 0.85,
      obrigatoria: true,
    }),
    zona({
      id: 'monobloco-direito',
      nome: `fita catraca monobloco do ${rotuloLadoVeiculo('direito')}`,
      tipo: 'estrutura',
      equipamento: 'fitaCatraca',
      equipamentosCompativeis: ['fitaCatraca'],
      etapa: 'secundaria',
      centro: { x: xFora, z: traseira * 0.55 },
      pontoSolo: [xFora, 0, traseira * 0.55],
      pontoVeiculo: [meiaLargura, 1.2, traseira * 0.55],
      raio: 0.85,
      obrigatoria: true,
    }),
    zona({
      id: 'escora-complexa-esquerda',
      nome: `V-Strut do ${rotuloLadoVeiculo('esquerdo')} do balanço suspenso`,
      tipo: 'escora',
      equipamento: 'vstrut',
      equipamentosCompativeis: ['vstrut'],
      etapa: 'secundaria',
      centro: { x: -(xFora + 0.2), z: traseira * 0.65 },
      pontoSolo: [-(xFora + 0.2), 0, traseira * 0.65],
      pontoVeiculo: [-meiaLargura, 1.1, traseira * 0.65],
      raio: 0.85,
      obrigatoria: true,
    }),
    zona({
      id: 'escora-complexa-direita',
      nome: `V-Strut do ${rotuloLadoVeiculo('direito')} do balanço suspenso`,
      tipo: 'escora',
      equipamento: 'vstrut',
      equipamentosCompativeis: ['vstrut'],
      etapa: 'secundaria',
      centro: { x: xFora + 0.2, z: frente * 0.65 },
      pontoSolo: [xFora + 0.2, 0, frente * 0.65],
      pontoVeiculo: [meiaLargura, 1.1, frente * 0.65],
      raio: 0.85,
      obrigatoria: true,
    }),
  ]
}

export type EstadoColocacao = 'correta' | 'proxima' | 'fora' | 'naoPrevista'

export interface LeituraColocacao {
  colocacao: ColocacaoEquipamento
  estado: EstadoColocacao
  zona: ZonaEstabilizacao | null
  distancia: number | null
  mensagem: string
}

export interface RigidezSuspensaoProgressiva {
  roll: number
  pitch: number
  vertical: number
  longitudinal: number
  detalhesPontos?: Record<string, number>
  /** Vínculos verticais independentes já criados sob a estrutura do veículo. */
  apoiosLocais?: readonly ApoioLocalSuspensao[]
}

export interface ApoioLocalSuspensao {
  id: string
  x: number
  z: number
  firmeza: number
}

export interface AvaliacaoEstabilizacao {
  progresso: number
  primariaConcluida: boolean
  secundariaConcluida: boolean
  zonasCobertas: readonly string[]
  leituras: readonly LeituraColocacao[]
  mensagem: string
  suspensaoAnulada: boolean
  rolamentoTravado: boolean
  motivoSuspensao: string
  rigidezProgressiva: RigidezSuspensaoProgressiva
}

export function equipamentoCompativelComZona(
  zona: ZonaEstabilizacao,
  equipamento: EquipamentoEstabilizacao,
  posicao: PosicaoEstabilizacao,
): boolean {
  if (zona.equipamento === equipamento) return true

  if (posicao === 'quatroRodas') {
    if (zona.id.startsWith('calco-coluna') && (equipamento === 'calco' || equipamento === 'bloco')) return true
    if (zona.id.startsWith('ajuste-bloco') && (equipamento === 'bloco' || equipamento === 'calco')) return true
  }

  if (posicao === 'tombado') {
    if ((zona.id === 'base-dianteira' || zona.id === 'base-traseira') && (equipamento === 'bloco' || equipamento === 'calco')) return true
    if (zona.id === 'folga-tombado' && (equipamento === 'cunha' || equipamento === 'bloco')) return true
  }

  if (posicao === 'capotado') {
    if ((zona.id.startsWith('capo-') || zona.id.startsWith('traseira-')) && (equipamento === 'bloco' || equipamento === 'calco')) return true
    if (zona.id.startsWith('ajuste-capotado') && (equipamento === 'cunha' || equipamento === 'bloco')) return true
  }

  if (posicao === 'sobreOutroVeiculo') {
    if (zona.id.startsWith('inferior-') && (equipamento === 'bloco' || equipamento === 'calco')) return true
    if (zona.id.startsWith('vazio-entre') && (equipamento === 'cunha' || equipamento === 'bloco')) return true
  }

  return false
}

export function instrucaoCliqueDaPosicao(posicao: PosicaoEstabilizacao): string {
  switch (posicao) {
    case 'quatroRodas':
      return 'Calços escalonados e blocos sob as bases das colunas (A/B/C); cunhas travando o rolamento nas rodas.'
    case 'tombado':
      return 'Calços sob as colunas A e C. Posicione o V-Strut na região destacada; o contato e a cinta são ligados automaticamente na maquete. Não use a roda como ancoragem.'
    case 'capotado':
      return 'Bloco entre o motor e a coluna A; calços sob o teto. Posicione as escoras nas regiões destacadas; a maquete liga automaticamente o contato e a cinta à estrutura.'
    case 'sobreOutroVeiculo':
      return 'Calce o veículo inferior, preencha vazios intermediários e amarre o conjunto em monobloco com fitas catraca.'
  }
}

const distancia = (a: PontoSolo, b: PontoSolo) => Math.hypot(a.x - b.x, a.z - b.z)

/**
 * Encontra a zona de estabilização estrutural ou vazio ideal para fazer snap
 * com base na posição do clique/ponteiro no solo e equipamento selecionado.
 */
export function encontrarZonaDeSnap(
  posicao: PosicaoEstabilizacao,
  perfil: PerfilVeiculo,
  equipamento: EquipamentoEstabilizacao,
  ponto: PontoSolo,
  cobertas?: ReadonlySet<string>,
): ZonaEstabilizacao | null {
  const zonas = zonasDaEstabilizacao(posicao, perfil)
  const candidatas = zonas
    .filter((z) => {
      if (cobertas && cobertas.has(z.id)) return false
      return equipamentoCompativelComZona(z, equipamento, posicao)
    })
    .map((z) => ({ zona: z, distancia: distancia(z.centro, ponto) }))
    .sort((a, b) => a.distancia - b.distancia)

  const melhor = candidatas[0]
  if (!melhor) return null
  const raioSnap = Math.max(melhor.zona.raio * 1.6, 0.8)
  if (melhor.distancia <= raioSnap) {
    return melhor.zona
  }
  return null
}

export function avaliarColocacoes(
  posicao: PosicaoEstabilizacao,
  perfil: PerfilVeiculo,
  colocacoes: readonly ColocacaoEquipamento[],
): AvaliacaoEstabilizacao {
  const zonas = zonasDaEstabilizacao(posicao, perfil)
  const cobertas = new Set<string>()
  const leituras: LeituraColocacao[] = []

  for (const colocacao of colocacoes) {
    if (colocacao.equipamento === 'fitaCatraca' && colocacao.ligacao) {
      const montagem = validarLigacao(colocacao, pontosMontagem(posicao, perfil))
      const zonaLigacao = zonas.find(z => z.id === montagem.zona)
      const cobriu = montagem.completa && zonaLigacao && !cobertas.has(zonaLigacao.id)
      if (cobriu) cobertas.add(zonaLigacao.id)
      leituras.push({ colocacao, estado: cobriu ? 'correta' : 'naoPrevista', zona: zonaLigacao ?? null, distancia: null,
        mensagem: montagem.mensagem + (montagem.completa && !cobriu ? ' Não acrescenta outro apoio previsto na avaliação deste cenário.' : '') })
      continue
    }
    const candidatas = zonas
      .filter((z) => {
        if (cobertas.has(z.id)) return false
        return equipamentoCompativelComZona(z, colocacao.equipamento, posicao)
      })
      .map((z) => ({ zona: z, distancia: distancia(z.centro, colocacao.posicao) }))
      .sort((a, b) => a.distancia - b.distancia)
    const melhor = candidatas[0]

    if (!melhor) {
      leituras.push({
        colocacao,
        estado: 'naoPrevista',
        zona: null,
        distancia: null,
        mensagem: 'Este recurso não é necessário em outro ponto deste cenário.',
      })
      continue
    }

    if (melhor.distancia <= melhor.zona.raio) {
      cobertas.add(melhor.zona.id)
      leituras.push({
        colocacao,
        estado: 'correta',
        zona: melhor.zona,
        distancia: melhor.distancia,
        mensagem: `Boa região: ${melhor.zona.nome}.`,
      })
    } else if (melhor.distancia <= melhor.zona.raio * 1.8) {
      leituras.push({
        colocacao,
        estado: 'proxima',
        zona: melhor.zona,
        distancia: melhor.distancia,
        mensagem: `Está próximo de ${melhor.zona.nome}, mas ainda há folga.`,
      })
    } else {
      leituras.push({
        colocacao,
        estado: 'fora',
        zona: melhor.zona,
        distancia: melhor.distancia,
        mensagem: `Fora da região funcional. Procure ${melhor.zona.nome}.`,
      })
    }
  }

  const firmezaPorZona: Record<string, number> = {}
  for (const leitura of leituras) {
    if ((leitura.estado === 'correta' || leitura.estado === 'proxima') && leitura.zona) {
      if (leitura.colocacao.equipamento === 'calco') {
        firmezaPorZona[leitura.zona.id] = leitura.colocacao.comCunhaAjuste ? 1.0 : 0.9
      } else {
        firmezaPorZona[leitura.zona.id] = 1.0
      }
    }
  }

  switch (posicao) {
    case 'quatroRodas':
      return avaliarQuatroRodas(zonas, cobertas, leituras, firmezaPorZona)
    case 'tombado':
      return avaliarTombado(zonas, cobertas, leituras, firmezaPorZona)
    case 'capotado':
      return avaliarCapotado(zonas, cobertas, leituras, firmezaPorZona)
    case 'sobreOutroVeiculo':
      return avaliarSobreOutroVeiculo(zonas, cobertas, leituras, firmezaPorZona)
  }
}

/** Avaliação da estabilização sobre quatro rodas: 3 ou 4 pontos nas colunas + calçamento de rodas. */
function avaliarQuatroRodas(
  zonas: readonly ZonaEstabilizacao[],
  cobertas: Set<string>,
  leituras: readonly LeituraColocacao[],
  firmezaPorZona: Record<string, number>,
): AvaliacaoEstabilizacao {
  const temAEsq = cobertas.has('calco-coluna-a-esq')
  const temBEsq = cobertas.has('calco-coluna-b-esq')
  const temCEsq = cobertas.has('calco-coluna-c-esq')
  const temADir = cobertas.has('calco-coluna-a-dir')
  const temBDir = cobertas.has('calco-coluna-b-dir')
  const temCDir = cobertas.has('calco-coluna-c-dir')

  const triangulo1 = temAEsq && temCEsq && temBDir
  const triangulo2 = temADir && temCDir && temBEsq
  const retangulo4Pontos = temAEsq && temCEsq && temADir && temCDir

  const suspensaoAnulada = triangulo1 || triangulo2 || retangulo4Pontos

  // Estabilização Horizontal: 2 rodas diagonalmente opostas com 2 cunhas por roda (frente + trás)
  const rodaDiantEsqBloqueada = cobertas.has('cunha-roda-diant-esq-frente') && cobertas.has('cunha-roda-diant-esq-tras')
  const rodaDiantDirBloqueada = cobertas.has('cunha-roda-diant-dir-frente') && cobertas.has('cunha-roda-diant-dir-tras')
  const rodaTrasEsqBloqueada = cobertas.has('cunha-roda-tras-esq-frente') && cobertas.has('cunha-roda-tras-esq-tras')
  const rodaTrasDirBloqueada = cobertas.has('cunha-roda-tras-dir-frente') && cobertas.has('cunha-roda-tras-dir-tras')

  // Apenas as duas diagonais válidas são aceitas:
  // 1) Dianteira motorista (esq) + Traseira passageiro (dir)
  // 2) Dianteira passageiro (dir) + Traseira motorista (esq)
  const diagonal1 = rodaDiantEsqBloqueada && rodaTrasDirBloqueada
  const diagonal2 = rodaDiantDirBloqueada && rodaTrasEsqBloqueada
  const rolamentoTravado = diagonal1 || diagonal2

  const cunhasTotal = [
    'cunha-roda-diant-esq-frente',
    'cunha-roda-diant-esq-tras',
    'cunha-roda-diant-dir-frente',
    'cunha-roda-diant-dir-tras',
    'cunha-roda-tras-esq-frente',
    'cunha-roda-tras-esq-tras',
    'cunha-roda-tras-dir-frente',
    'cunha-roda-tras-dir-tras',
  ].filter((id) => cobertas.has(id)).length

  const pAEsq = firmezaPorZona['calco-coluna-a-esq'] ?? 0
  const pBEsq = firmezaPorZona['calco-coluna-b-esq'] ?? 0
  const pCEsq = firmezaPorZona['calco-coluna-c-esq'] ?? 0
  const pADir = firmezaPorZona['calco-coluna-a-dir'] ?? 0
  const pBDir = firmezaPorZona['calco-coluna-b-dir'] ?? 0
  const pCDir = firmezaPorZona['calco-coluna-c-dir'] ?? 0

  const ladoEsq = Math.max(pAEsq, pBEsq, pCEsq)
  const ladoDir = Math.max(pADir, pBDir, pCDir)
  const rigidezRoll = ladoEsq > 0 && ladoDir > 0
    ? (ladoEsq + ladoDir) / 2
    : Math.max(ladoEsq, ladoDir) * 0.6

  const frente = Math.max(pAEsq, pADir)
  const traseira = Math.max(pCEsq, pCDir)
  const centro = Math.max(pBEsq, pBDir)
  const rigidezPitch = Math.min(1, frente * 0.45 + traseira * 0.45 + centro * 0.35)

  const apoiosValores = [pAEsq, pBEsq, pCEsq, pADir, pBDir, pCDir].filter((v) => v > 0)
  const rigidezVertical = apoiosValores.length === 0
    ? 0
    : Math.min(1, (apoiosValores.reduce((a, b) => a + b, 0) / 3))

  const rigidezLongitudinal = rolamentoTravado ? 1 : Math.min(0.8, cunhasTotal * 0.2)
  const apoiosLocais = zonas
    .filter((zona) => zona.id.startsWith('calco-coluna') && cobertas.has(zona.id))
    .flatMap((zona) => {
      const firmeza = firmezaPorZona[zona.id]
      return firmeza === undefined
        ? []
        : [{ id: zona.id, x: zona.centro.x, z: zona.centro.z, firmeza: 1 }]
    })

  const rigidezProgressiva: RigidezSuspensaoProgressiva = suspensaoAnulada && rolamentoTravado
    ? {
        roll: 1,
        pitch: 1,
        vertical: 1,
        longitudinal: 1,
        detalhesPontos: firmezaPorZona,
        apoiosLocais,
      }
    : {
        roll: Number(rigidezRoll.toFixed(2)),
        pitch: Number(rigidezPitch.toFixed(2)),
        vertical: Number(rigidezVertical.toFixed(2)),
        longitudinal: Number(rigidezLongitudinal.toFixed(2)),
        detalhesPontos: firmezaPorZona,
        apoiosLocais,
      }

  const primariaConcluida = suspensaoAnulada && rolamentoTravado
  const secundariaConcluida = primariaConcluida && (cobertas.has('ajuste-bloco-esq') || cobertas.has('ajuste-bloco-dir'))

  const progresso = (suspensaoAnulada ? 0.5 : 0) + (rolamentoTravado ? 0.35 : 0) + (secundariaConcluida ? 0.15 : 0)
  const motivoSuspensao = suspensaoAnulada
    ? 'Suspensão anulada: calços sob as colunas (A/B/C) travam o movimento elástico das molas.'
    : 'Suspensão ativa: veículo suscetível a balanço e vibrações que agravam o trauma raquimedular (TRM).'

  return {
    progresso: Math.min(1, progresso),
    primariaConcluida,
    secundariaConcluida,
    zonasCobertas: [...cobertas],
    leituras,
    suspensaoAnulada,
    rolamentoTravado,
    motivoSuspensao,
    rigidezProgressiva,
    mensagem: secundariaConcluida
      ? 'Cenário concluído com sucesso. Calçamento rígido e rolamento neutralizado.'
      : primariaConcluida
        ? 'Estabilização primária concluída (suspensão anulada). Ajuste a etapa secundária.'
        : 'Estabilização horizontal: bloqueie 2 rodas em diagonal oposta (2 cunhas por roda, frente e trás). Estabilização vertical: calços sob as colunas A, B e C.',
  }
}

/** Avaliação da estabilização de veículo tombado: base inferior (assoalho) + escoras V-Strut no teto + amarra. */
function avaliarTombado(
  zonas: readonly ZonaEstabilizacao[],
  cobertas: Set<string>,
  leituras: readonly LeituraColocacao[],
  firmezaPorZona: Record<string, number>,
): AvaliacaoEstabilizacao {
  const baseDiant = cobertas.has('base-dianteira')
  const baseTras = cobertas.has('base-traseira')
  const baseInferiorOk = baseDiant && baseTras

  const escoraDiant = cobertas.has('escora-dianteira')
  const escoraTras = cobertas.has('escora-traseira')
  const escorasOk = escoraDiant && escoraTras

  const amarraOk = cobertas.has('amarra-tombado')
  const folgaOk = cobertas.has('folga-tombado')

  const primariaConcluida = baseInferiorOk && escorasOk
  const secundariaConcluida = primariaConcluida && amarraOk && folgaOk

  const progresso =
    (baseDiant ? 0.2 : 0) +
    (baseTras ? 0.2 : 0) +
    (escoraDiant ? 0.2 : 0) +
    (escoraTras ? 0.2 : 0) +
    (amarraOk ? 0.1 : 0) +
    (folgaOk ? 0.1 : 0)

  const rollBase = baseInferiorOk ? 0.45 : (baseDiant || baseTras ? 0.25 : 0)
  const rollEscoras = escorasOk ? 0.45 : (escoraDiant || escoraTras ? 0.25 : 0)
  const rollAmarra = amarraOk ? 0.1 : 0
  const rigidezRoll = secundariaConcluida ? 1 : Math.min(1, rollBase + rollEscoras + rollAmarra)

  const rigidezPitch = Number((((baseDiant ? 0.5 : 0) + (baseTras ? 0.5 : 0)) * 0.6 + ((escoraDiant ? 0.5 : 0) + (escoraTras ? 0.5 : 0)) * 0.4).toFixed(2))
  const rigidezVertical = Number(((baseInferiorOk ? 0.6 : 0.3) + (escorasOk ? 0.4 : 0.2)).toFixed(2))
  const rigidezLongitudinal = Number(((amarraOk ? 0.6 : 0) + (folgaOk ? 0.4 : 0)).toFixed(2))

  const apoiosLocais = zonas
    .filter((z) => cobertas.has(z.id))
    .map((z) => ({ id: z.id, x: z.centro.x, z: z.centro.z, firmeza: firmezaPorZona[z.id] ?? 1 }))

  const rigidezProgressiva: RigidezSuspensaoProgressiva = secundariaConcluida
    ? { roll: 1, pitch: 1, vertical: 1, longitudinal: 1, detalhesPontos: firmezaPorZona, apoiosLocais }
    : {
        roll: Number(rigidezRoll.toFixed(2)),
        pitch: rigidezPitch,
        vertical: rigidezVertical,
        longitudinal: rigidezLongitudinal,
        detalhesPontos: firmezaPorZona,
        apoiosLocais,
      }

  const motivoSuspensao = primariaConcluida
    ? 'Tombamento lateral neutralizado: colunas apoiadas com calços escalonados e assoalho travado com escoras V-Strut.'
    : 'Instabilidade lateral crítica: centro de gravidade elevado sem triângulo de sustentação.'

  return {
    progresso: Math.min(1, Number(progresso.toFixed(2))),
    primariaConcluida,
    secundariaConcluida,
    zonasCobertas: [...cobertas],
    leituras,
    suspensaoAnulada: true,
    rolamentoTravado: primariaConcluida,
    motivoSuspensao,
    rigidezProgressiva,
    mensagem: secundariaConcluida
      ? 'Veículo tombado estabilizado com sucesso: base calçada, escoras em 45°-60° e fita catraca tensionada.'
      : primariaConcluida
        ? 'Estabilização primária concluída (base e escoras). Tensione a fita catraca e trave folgas com cunha.'
        : 'Calce a base inferior (assoalho) com blocos e apoie escoras V-Strut no teto para travar o tombamento.',
  }
}

/** Avaliação da estabilização de veículo capotado: calçamento do capô e anulação da gangorra na traseira elevada. */
function avaliarCapotado(
  zonas: readonly ZonaEstabilizacao[],
  cobertas: Set<string>,
  leituras: readonly LeituraColocacao[],
  firmezaPorZona: Record<string, number>,
): AvaliacaoEstabilizacao {
  const capoEsq = cobertas.has('capo-esquerdo')
  const capoDir = cobertas.has('capo-direito')
  const dianteiraOk = capoEsq && capoDir

  const trasEsq = cobertas.has('traseira-esquerda')
  const trasDir = cobertas.has('traseira-direita')
  const traseiraOk = trasEsq && trasDir

  const cunhaFrente = cobertas.has('ajuste-capotado-frente')
  const cunhaTras = cobertas.has('ajuste-capotado-traseira')

  const escoraEsq = cobertas.has('escora-capotado-esquerda')
  const escoraDir = cobertas.has('escora-capotado-direita') || cobertas.has('escora-capotado')
  const escorasOk = escoraEsq && escoraDir

  const primariaConcluida = dianteiraOk && traseiraOk
  const secundariaConcluida = primariaConcluida && cunhaFrente && cunhaTras

  const progresso =
    (capoEsq ? 0.15 : 0) +
    (capoDir ? 0.15 : 0) +
    (trasEsq ? 0.25 : 0) +
    (trasDir ? 0.25 : 0) +
    (cunhaFrente ? 0.10 : 0) +
    (cunhaTras ? 0.10 : 0) +
    (escoraEsq ? 0.05 : 0) +
    (escoraDir ? 0.05 : 0)

  // Na gangorra, a rigidez de pitch depende criticamente da traseira elevada estar apoiada
  const rigidezPitch = escorasOk || (traseiraOk && dianteiraOk)
    ? 1.0
    : traseiraOk || dianteiraOk
      ? 0.45
      : 0.15

  const ladoEsq = (capoEsq ? 0.35 : 0) + (trasEsq ? 0.35 : 0) + (escoraEsq ? 0.3 : 0)
  const ladoDir = (capoDir ? 0.35 : 0) + (trasDir ? 0.35 : 0) + (escoraDir ? 0.3 : 0)
  const rigidezRoll = Number(((ladoEsq + ladoDir) / 2).toFixed(2))
  const rigidezVertical = Number(((dianteiraOk ? 0.4 : 0.2) + (traseiraOk ? 0.4 : 0.2) + (escorasOk ? 0.2 : 0)).toFixed(2))
  const rigidezLongitudinal = Number(((cunhaFrente ? 0.4 : 0) + (cunhaTras ? 0.4 : 0) + (escorasOk ? 0.2 : 0)).toFixed(2))

  const apoiosLocais = zonas
    .filter((z) => cobertas.has(z.id))
    .map((z) => ({ id: z.id, x: z.centro.x, z: z.centro.z, firmeza: firmezaPorZona[z.id] ?? 1 }))

  const rigidezProgressiva: RigidezSuspensaoProgressiva = secundariaConcluida
    ? { roll: 1, pitch: 1, vertical: 1, longitudinal: 1, detalhesPontos: firmezaPorZona, apoiosLocais }
    : {
        roll: rigidezRoll,
        pitch: rigidezPitch,
        vertical: rigidezVertical,
        longitudinal: rigidezLongitudinal,
        detalhesPontos: firmezaPorZona,
        apoiosLocais,
      }

  const motivoSuspensao = escorasOk
    ? 'Efeito gangorra neutralizado: bloco dianteiro, calços sob o teto e V-Struts travando as rodas traseiras.'
    : primariaConcluida
      ? 'Efeito gangorra neutralizado: bloco entre motor/coluna A e calços escalonados sob o teto.'
      : 'Efeito gangorra ativo: veículo instável sobre teto e cofre sem apoios complementares adequados.'

  return {
    progresso: Math.min(1, Number(progresso.toFixed(2))),
    primariaConcluida,
    secundariaConcluida,
    zonasCobertas: [...cobertas],
    leituras,
    suspensaoAnulada: true,
    rolamentoTravado: primariaConcluida,
    motivoSuspensao,
    rigidezProgressiva,
    mensagem: secundariaConcluida
      ? escorasOk
        ? 'Veículo capotado estabilizado com excelência: bloco frontal, calços sob o teto com cunhas e V-Struts travando a traseira.'
        : 'Veículo capotado estabilizado com sucesso: bloco (motor/coluna A) e calços escalonados sob o teto com cunhas de ajuste.'
      : primariaConcluida
        ? 'Estabilização primária concluída (bloco e calços de teto posicionados). Elimine as folgas com cunhas e instale os V-Struts traseiros.'
        : 'Posicione blocos entre motor e coluna A, calços escalonados sob o teto e V-Struts nas rodas traseiras para anular a gangorra.',
  }
}

/** Avaliação da estabilização em cenário complexo: calçamento inferior + preenchimento de vazios + união monobloco com fitas catraca. */
function avaliarSobreOutroVeiculo(
  zonas: readonly ZonaEstabilizacao[],
  cobertas: Set<string>,
  leituras: readonly LeituraColocacao[],
  firmezaPorZona: Record<string, number>,
): AvaliacaoEstabilizacao {
  const infFrente = cobertas.has('inferior-frente')
  const infTras = cobertas.has('inferior-traseira')
  const inferiorOk = infFrente && infTras

  const vazioFrente = cobertas.has('vazio-entre-frente')
  const vazioTras = cobertas.has('vazio-entre-traseira')
  const vaziosOk = vazioFrente && vazioTras

  const monoblocoEsq = cobertas.has('monobloco-esquerdo')
  const monoblocoDir = cobertas.has('monobloco-direito')
  const monoblocoOk = monoblocoEsq && monoblocoDir

  const escoraEsq = cobertas.has('escora-complexa-esquerda')
  const escoraDir = cobertas.has('escora-complexa-direita')
  const escorasOk = escoraEsq && escoraDir

  const primariaConcluida = inferiorOk && vaziosOk
  const secundariaConcluida = primariaConcluida && monoblocoOk && escorasOk

  const progresso =
    (infFrente ? 0.15 : 0) +
    (infTras ? 0.15 : 0) +
    (vazioFrente ? 0.15 : 0) +
    (vazioTras ? 0.15 : 0) +
    (monoblocoEsq ? 0.125 : 0) +
    (monoblocoDir ? 0.125 : 0) +
    (escoraEsq ? 0.075 : 0) +
    (escoraDir ? 0.075 : 0)

  const rigidezRoll = Number(((inferiorOk ? 0.35 : 0) + (monoblocoOk ? 0.45 : 0) + (escorasOk ? 0.2 : 0)).toFixed(2))
  const rigidezPitch = Number(((vaziosOk ? 0.45 : 0) + (monoblocoOk ? 0.35 : 0) + (escorasOk ? 0.2 : 0)).toFixed(2))
  const rigidezVertical = Number(((inferiorOk ? 0.5 : 0) + (vaziosOk ? 0.5 : 0)).toFixed(2))
  const rigidezLongitudinal = Number(((monoblocoOk ? 0.7 : 0) + (vaziosOk ? 0.3 : 0)).toFixed(2))

  const apoiosLocais = zonas
    .filter((z) => cobertas.has(z.id))
    .map((z) => ({ id: z.id, x: z.centro.x, z: z.centro.z, firmeza: firmezaPorZona[z.id] ?? 1 }))

  const rigidezProgressiva: RigidezSuspensaoProgressiva = secundariaConcluida
    ? { roll: 1, pitch: 1, vertical: 1, longitudinal: 1, detalhesPontos: firmezaPorZona, apoiosLocais }
    : {
        roll: rigidezRoll,
        pitch: rigidezPitch,
        vertical: rigidezVertical,
        longitudinal: rigidezLongitudinal,
        detalhesPontos: firmezaPorZona,
        apoiosLocais,
      }

  const motivoSuspensao = secundariaConcluida
    ? 'Conjunto unificado em monobloco: fitas catraca e escoras travam o movimento relativo entre veículos.'
    : 'Risco de deslizamento e colapso: veículos desvinculados podem escorregar durante o desencarceramento.'

  return {
    progresso: Math.min(1, Number(progresso.toFixed(2))),
    primariaConcluida,
    secundariaConcluida,
    zonasCobertas: [...cobertas],
    leituras,
    suspensaoAnulada: inferiorOk,
    rolamentoTravado: monoblocoOk,
    motivoSuspensao,
    rigidezProgressiva,
    mensagem: secundariaConcluida
      ? 'Cenário complexo estabilizado com sucesso: conjunto unificado em monobloco rígido.'
      : primariaConcluida
        ? 'Estabilização primária concluída. Amarre os veículos em monobloco com fitas catraca e fixe as escoras.'
        : 'Calce a base do veículo inferior e preencha os vazios entre eles com blocos e cunhas.',
  }
}

export function vetoresDeInstabilidade(
  posicao: PosicaoEstabilizacao,
  perfil: PerfilVeiculo,
): readonly VetorInstabilidade[] {
  if (posicao === 'quatroRodas') {
    return [
      { id: 'rolamento-frente', origem: [0, 0.55, 0.45], destino: [0, 0.55, 2.1], leitura: 'rolamento' },
      { id: 'rolamento-tras', origem: [0, 0.55, -0.45], destino: [0, 0.55, -2.1], leitura: 'rolamento' },
      {
        id: 'balanco-suspensao',
        origem: [0, perfil.tetoY + 0.05, 0],
        destino: [0, perfil.tetoY + 0.45, 0],
        leitura: 'balanço da suspensão',
      },
    ]
  }
  if (posicao === 'tombado') {
    return [
      // o teto está em +X: o veículo tomba para o lado do assoalho exposto (−X)
      { id: 'tombamento', origem: [-0.15, perfil.largura / 2 + 0.15, 0], destino: [-1.8, 0.55, 0], leitura: 'tombamento lateral' },
    ]
  }
  if (posicao === 'capotado') {
    return [
      { id: 'balanco-frente', origem: [0, perfil.tetoY + 0.35, 0.35], destino: [0, perfil.tetoY + 0.15, 2], leitura: 'balanço longitudinal' },
      { id: 'balanco-tras', origem: [0, perfil.tetoY + 0.35, -0.35], destino: [0, perfil.tetoY + 0.15, -2], leitura: 'balanço longitudinal' },
    ]
  }
  return [
    { id: 'deslizamento', origem: [0, perfil.tetoY * 1.8, 0], destino: [1.8, perfil.tetoY * 1.55, 0.3], leitura: 'deslizamento do veículo superior' },
    { id: 'separacao', origem: [0, perfil.tetoY, 0], destino: [-1.6, perfil.tetoY + 0.4, 0], leitura: 'separação do conjunto' },
  ]
}

export function cenarioLiberado(
  posicao: PosicaoEstabilizacao,
  concluidos: readonly PosicaoEstabilizacao[],
): boolean {
  const nivel = POSICOES_ESTABILIZACAO[posicao].nivel
  return IDS_POSICAO_ESTABILIZACAO
    .filter((id) => POSICOES_ESTABILIZACAO[id].nivel < nivel)
    .every((id) => concluidos.includes(id))
}
